/**
 * App-wide ErrorBoundary.
 *
 * Wraps every Route so a render crash on a single page yields a small
 * friendly fallback instead of the dreaded "white screen of death".
 *
 * Triggered by: TypeError on null fields, async unhandled in render,
 * lib-level throws (Sonner with a non-string body, jspdf with a missing
 * font, etc.).
 *
 * The user can either:
 *   • Press "Reload" → triggers a soft reset of the route component.
 *   • Press "Back to dashboard" → navigates home.
 */
import React from 'react';
import { AlertTriangle, RotateCcw, Home } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, resetKey: 0 };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', error, info);
  }

  reset = () => {
    this.setState((s) => ({ hasError: false, error: null, resetKey: s.resetKey + 1 }));
  };

  goHome = () => {
    window.location.assign('/');
  };

  render() {
    if (this.state.hasError) {
      const msg = String(this.state.error?.message || this.state.error || 'Unknown error').slice(0, 240);
      return (
        <div data-testid="error-boundary" className="min-h-[60vh] flex items-center justify-center p-8">
          <div className="max-w-lg w-full bg-white border border-rose-200 rounded-xl p-6 shadow-sm" dir="rtl">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-rose-50 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-rose-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-bold text-slate-900">
                  حدث خطأ أثناء عرض الصفحة
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  لا تقلق — لم تُفقَد بياناتك. تم منع الـ crash وحفظ الحالة. حاول التحديث أو العودة للوحة.
                </p>
                <details className="mt-3 text-xs">
                  <summary className="cursor-pointer text-slate-500 hover:text-slate-700">
                    التفاصيل التقنية (للمطوّر)
                  </summary>
                  <pre className="mt-2 bg-slate-50 border border-slate-200 rounded p-2 overflow-x-auto text-rose-700 text-[11px] font-mono">
                    {msg}
                  </pre>
                </details>
                <div className="flex gap-2 mt-4">
                  <button data-testid="error-reload" onClick={this.reset}
                    className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 text-white px-3 py-2 text-sm font-medium hover:bg-indigo-700">
                    <RotateCcw className="h-4 w-4" /> إعادة المحاولة
                  </button>
                  <button data-testid="error-home" onClick={this.goHome}
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                    <Home className="h-4 w-4" /> العودة للوحة
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>;
  }
}

export default ErrorBoundary;
